import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScoreBadge } from './ScoreBadge';
import {
  type ColumnDef,
  SortableHeader,
  filterItems,
  renderColumnFilter,
  sortItems,
  usePersistedColumnFilters,
  usePersistedColumnVisibility,
} from './tenderTableUtils';
import { TenderTableToolbar } from './TenderTableToolbar';
import { type TenderResult, tenderKey, tenderScore } from './types';

const FILTER_STORAGE_KEY = 'atek-rejected-tenders-column-filters';
const VISIBILITY_STORAGE_KEY = 'atek-rejected-tenders-column-visibility';
const PAGE_SIZE = 50;

interface RejectedTendersTableProps {
  items: TenderResult[];
  isRefreshing?: boolean;
  onRefresh: () => void;
  onRowClick: (t: TenderResult) => void;
  onRestore: (t: TenderResult) => void;
}

export function RejectedTendersTable({
  items,
  isRefreshing,
  onRefresh,
  onRowClick,
  onRestore,
}: RejectedTendersTableProps) {
  const [sortKey, setSortKey] = useState<string | null>('score');
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);
  const [page, setPage] = useState(0);

  const sourceOptions = useMemo(() => {
    const set = new Set<string>();
    for (const t of items) if (t.source) set.add(t.source);
    return Array.from(set).sort().map((v) => ({ label: v, value: v }));
  }, [items]);

  const columns: ColumnDef<TenderResult>[] = useMemo(() => ([
    {
      key: 'score',
      label: 'Score',
      sortKey: 'score',
      filterType: 'number-range',
      getValue: (t) => tenderScore(t),
      render: (t) => <ScoreBadge score={tenderScore(t)} />,
      headerClassName: 'w-[90px]',
    },
    {
      key: 'name',
      label: 'Title',
      sortKey: 'name',
      filterType: 'text',
      getValue: (t) => t.name,
      render: (t) => (
        <div className="max-w-sm">
          <p className="font-medium text-xs break-words">{t.name}</p>
          {t.summary && <p className="text-xs text-muted-foreground break-words mt-0.5">{t.summary}</p>}
        </div>
      ),
    },
    {
      key: 'organisationName',
      label: 'Organisation',
      sortKey: 'organisationName',
      filterType: 'text',
      getValue: (t) => t.organisationName ?? '',
      render: (t) => <span className="text-sm">{t.organisationName ?? '—'}</span>,
    },
    {
      key: 'source',
      label: 'Source',
      filterType: 'select',
      filterOptions: sourceOptions,
      getValue: (t) => t.source,
      render: (t) => <Badge variant="outline" className="text-xs">{t.source}</Badge>,
    },
    {
      key: 'publishDate',
      label: 'Published',
      sortKey: 'publishDate',
      filterType: 'date-range',
      getValue: (t) => t.publishDate ?? '',
      render: (t) => <span className="text-sm text-muted-foreground whitespace-nowrap">{t.publishDate ?? '—'}</span>,
    },
    {
      key: 'deadline',
      label: 'Deadline',
      sortKey: 'deadline',
      filterType: 'date-range',
      getValue: (t) => t.deadline ?? '',
      render: (t) => <span className="text-sm text-muted-foreground whitespace-nowrap">{t.deadline ?? '—'}</span>,
    },
    {
      key: 'actions',
      label: 'Actions',
      noFilter: true,
      align: 'right',
      getValue: () => '',
      headerClassName: 'w-[90px] text-right',
      render: (t) => (
        <div className="flex items-center justify-end gap-0.5">
          <button
            className="p-1 rounded text-muted-foreground hover:text-green-600 transition-colors"
            title="Restore tender"
            onClick={(e) => { e.stopPropagation(); onRestore(t); }}
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
          <a
            href={t.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-blue-600 hover:text-blue-800 p-1"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      ),
    },
  ]), [sourceOptions, onRestore]);

  const {
    columnFilters, setColumnFilter, clearColumnFilters, hasActiveColumnFilters,
  } = usePersistedColumnFilters(FILTER_STORAGE_KEY);

  const { toggleColumn, isVisible } = usePersistedColumnVisibility(VISIBILITY_STORAGE_KEY, columns);

  const visibleColumns = useMemo(() => columns.filter((c) => c.noFilter || isVisible(c.key)), [columns, isVisible]);

  const filteredItems = useMemo(() => filterItems(items, columns, columnFilters), [items, columns, columnFilters]);
  const sortedItems = useMemo(() => sortItems(filteredItems, columns, sortKey, sortDirection), [filteredItems, columns, sortKey, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const pagedItems = useMemo(
    () => sortedItems.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE),
    [sortedItems, currentPage],
  );

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDirection((d) => (d === 1 ? -1 : 1));
    else { setSortKey(key); setSortDirection(-1); }
    setPage(0);
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <TenderTableToolbar
        items={sortedItems}
        columns={columns}
        csvFilename="rejected-tenders"
        hasActiveColumnFilters={hasActiveColumnFilters}
        onClearColumnFilters={clearColumnFilters}
        isVisible={isVisible}
        toggleColumn={toggleColumn}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
        countLabel={`Showing ${sortedItems.length} of ${items.length}`}
      />
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              {visibleColumns.map((col) => renderColumnFilter(col, columnFilters, setColumnFilter))}
            </TableRow>
            <TableRow>
              {visibleColumns.map((col) => (
                <TableHead
                  key={col.key}
                  className={cn(col.align === 'right' && 'text-right', col.headerClassName)}
                >
                  {col.sortKey ? (
                    <SortableHeader
                      label={col.label}
                      sortKey={col.sortKey}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      align={col.align}
                    />
                  ) : (
                    col.label
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="py-12 text-center text-muted-foreground">
                  {items.length === 0
                    ? 'No rejected tenders. Use the X button on any tender to dismiss it.'
                    : 'No rejected tenders match the filters'}
                </TableCell>
              </TableRow>
            )}
            {pagedItems.map((t, i) => (
              <TableRow
                key={`rejected-${tenderKey(t)}-${i}`}
                className="cursor-pointer opacity-60 hover:opacity-100 hover:bg-muted/50"
                onClick={() => onRowClick(t)}
              >
                {visibleColumns.map((col) => (
                  <TableCell
                    key={col.key}
                    className={cn(
                      'text-sm align-top',
                      col.align === 'right' && 'text-right tabular-nums',
                      col.className,
                    )}
                  >
                    {col.render ? col.render(t) : (String(col.getValue(t) ?? '') || '—')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {totalPages > 1 && (
        <div className="border-t px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {currentPage + 1} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 0}>
              Previous
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(currentPage + 1)} disabled={currentPage >= totalPages - 1}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
